import React, { useReducer } from 'react';

import type { NormalizedResource } from '@components/DynamicResourceForms/DynamicResourceForms';

import type { FhirResource } from '@models/fhir-resource';

// Curated Fhir Resource
export interface CuratedFhirResource extends FhirResource {
  discern_result_id: string;
  bboxId: string;
  nextId: string | null;
  formType?: string | null;
}

// Resource Management State
interface ResourceState {
  manualResources: NormalizedResource[];
  currentRecord: CuratedFhirResource | null;
}

// Actions
export enum ResourceActions {
  AddManualResource = 'addManualResource',
  RemoveManualResource = 'removeManualResource',
  SetRecord = 'setRecord',
}

// Action payloads
export type ResourcePayload = {
  [ResourceActions.AddManualResource]: { resource: NormalizedResource };
  [ResourceActions.RemoveManualResource]: { id: string };
  [ResourceActions.SetRecord]: { record: CuratedFhirResource | null };
};

// Reducer
function resourceReducer(
  state: ResourceState,
  action: { type: ResourceActions; [key: string]: any }
): ResourceState {
  switch (action.type) {
    case ResourceActions.AddManualResource:
      return {
        ...state,
        manualResources: [...state.manualResources, action.resource],
      };
    case ResourceActions.RemoveManualResource:
      return {
        ...state,
        manualResources: state.manualResources.filter(
          (r) => r.id !== action.id
        ),
      };
    case ResourceActions.SetRecord:
      return { ...state, currentRecord: action.record };
    default:
      return state;
  }
}

// Initial State
const initialResourceState: ResourceState = {
  manualResources: [],
  currentRecord: null,
};

// Context
export const AutoCurationContext = React.createContext<{
  state: ResourceState;
  dispatch: React.Dispatch<{ type: ResourceActions; [key: string]: any }>;
}>({
  state: initialResourceState,
  dispatch: () => null,
});

export const AutoCurationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(resourceReducer, initialResourceState);

  return (
    <AutoCurationContext.Provider value={{ state, dispatch }}>
      {children}
    </AutoCurationContext.Provider>
  );
};
